import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, Send } from 'lucide-react';
import { Modal, Button } from './ui';
import TomAssistantTrigger from './TomAssistantTrigger';
import { useScreenData } from '../hooks/useScreenData';
import {
  buildTomScreenPayload,
  callTomAssistant,
  tomAssistantErrorMessage,
  type TomMessage,
} from '../lib/tomAssistantCore';

interface TomTextAssistantProps {
  currentContext?: string;
}

export default function TomTextAssistant({ currentContext }: TomTextAssistantProps) {
  const location = useLocation();
  const { screenData } = useScreenData();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<TomMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen, thinking]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const t = window.setTimeout(() => inputRef.current?.focus(), 100);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  const sendMessage = useCallback(async () => {
    const text = draft.trim();
    if (!text || thinking) return;

    setError(null);
    setDraft('');
    setMessages((prev) => [...prev, { role: 'user', text, timestamp: new Date() }]);
    setThinking(true);

    try {
      const screenPayload = buildTomScreenPayload(
        screenData as Record<string, unknown> | null | undefined,
        location
      );
      const { reply } = await callTomAssistant(text, screenPayload, currentContext || '');
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: reply, timestamp: new Date() },
      ]);
    } catch (err) {
      setError(tomAssistantErrorMessage(err));
    } finally {
      setThinking(false);
    }
  }, [currentContext, draft, location, screenData, thinking]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  return (
    <>
      <TomAssistantTrigger
        onClick={() => setIsOpen(true)}
        title="Tom öffnen"
        ariaLabel="Tom Assistent öffnen"
      />

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Tom"
        size="lg"
        panelClassName="max-w-2xl"
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              disabled={thinking}
              placeholder="Ihre Frage…"
              className="min-h-[2.75rem] flex-1 resize-y rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
            <div className="flex shrink-0 gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setMessages([]);
                  setError(null);
                }}
                disabled={thinking || messages.length === 0}
              >
                Verlauf löschen
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => void sendMessage()}
                disabled={thinking || !draft.trim()}
              >
                {thinking ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Send className="h-4 w-4" aria-hidden />
                )}
                <span className="ml-1.5">Senden</span>
              </Button>
            </div>
          </div>
        }
      >
        <div className="flex min-h-[280px] flex-col">
          <div className="flex-1 space-y-3 overflow-y-auto pr-1">
            {messages.length === 0 ? (
              <p className="py-8 text-center text-base text-gray-700 dark:text-gray-200">
                Hallo, ich bin Tom, dein Assistent.
              </p>
            ) : (
              messages.map((message, index) => (
                <div
                  key={`${message.timestamp.getTime()}-${index}`}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                      message.role === 'user'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                    }`}
                  >
                    {message.text}
                  </div>
                </div>
              ))
            )}
            {thinking && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Tom denkt nach…
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {error ? (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
